import React, { useState } from 'react';
import { Building2, UserCircle } from 'lucide-react';


interface CompanyStep1NameProps {
    hirerType: 'company' | 'recruiter';
    companyName: string;
    onHirerTypeUpdate: (type: 'company' | 'recruiter') => void;
    onUpdate: (name: string) => void;
    onNext: () => void;
}

export const CompanyStep1Name: React.FC<CompanyStep1NameProps> = ({
    hirerType,
    companyName,
    onHirerTypeUpdate,
    onUpdate,
    onNext,
}) => {
    const [error, setError] = useState('');

    const handleNext = () => {
        if (!companyName.trim()) {
            setError(hirerType === 'company' ? 'Company name is required' : 'Name is required');
            return;
        }
        setError('');
        onNext();
    };

    const isCompany = hirerType === 'company';

    return (
        <div className="w-full max-w-md mx-auto">
            <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">Tell us about yourself</h1>

            {/* Hirer Type Selection */}
            <div className="mb-6 md:mb-8">
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2 md:mb-3">
                    I am a... <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                    <button
                        type="button"
                        onClick={() => onHirerTypeUpdate('company')}
                        className={`flex flex-col items-center justify-center p-4 md:p-6 rounded-xl border-2 transition-all ${isCompany
                                ? 'border-slate-900 bg-slate-50 text-slate-900'
                                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                    >
                        <Building2 className={`w-6 h-6 md:w-8 md:h-8 mb-1 md:mb-2 ${isCompany ? 'text-slate-900' : 'text-gray-400'}`} />
                        <span className="text-sm md:text-base font-semibold">Company</span>
                        <span className="text-[10px] md:text-xs mt-0.5 md:mt-1 text-gray-500 text-center">Hiring for your team</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => onHirerTypeUpdate('recruiter')}
                        className={`flex flex-col items-center justify-center p-4 md:p-6 rounded-xl border-2 transition-all ${!isCompany
                                ? 'border-slate-900 bg-slate-50 text-slate-900'
                                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                    >
                        <UserCircle className={`w-6 h-6 md:w-8 md:h-8 mb-1 md:mb-2 ${!isCompany ? 'text-slate-900' : 'text-gray-400'}`} />
                        <span className="text-sm md:text-base font-semibold">Recruiter</span>
                        <span className="text-[10px] md:text-xs mt-0.5 md:mt-1 text-gray-500 text-center">Hiring for clients</span>
                    </button>
                </div>
            </div>

            <div className="space-y-4 md:space-y-6">
                <div>
                    <label htmlFor="companyName" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                        {isCompany ? 'Company Name' : 'Your Name / Agency Name'} <span className="text-red-500">*</span>
                    </label>
                    <input
                        id="companyName"
                        type="text"
                        value={companyName}
                        onChange={(e) => {
                            setError('');
                            onUpdate(e.target.value);
                        }}
                        placeholder={isCompany ? 'e.g. Acme Corp' : 'e.g. John Smith or Smith Recruiting'}
                        className={`w-full px-3 md:px-4 py-2.5 md:py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors text-sm md:text-base ${error && !companyName.trim()
                            ? 'border-red-500 focus:ring-red-200'
                            : 'border-gray-300 focus:ring-slate-200 focus:border-slate-900'
                            }`}
                        autoFocus
                    />
                </div>

                {error && <p className="text-xs md:text-sm text-red-600 text-center">{error}</p>}

                <button
                    onClick={handleNext}
                    className="w-full bg-black text-white py-2.5 md:py-3 px-6 rounded-lg font-medium hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2 transition-colors text-sm md:text-base"
                >
                    Continue
                </button>
            </div>
        </div>
    );
};
